import './Modal.scss'
import './UniqueCustomerNameModal.css'

import { TextArea, TextAreaChangeEvent } from '@progress/kendo-react-inputs'
import { useCallback, useEffect, useState } from 'react'
import CreatableSelect from 'react-select/creatable'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import {
    AutocompleteBusinessUnitReturnPayload,
    AutocompleteEntityReturnPayload,
    AutocompleteSiteReturnPayload,
    fetchAutocompleteBusinessUnit,
    fetchAutocompleteEntity,
    fetchAutocompleteSite,
    fetchUniqueCustomer,
    fetchUniqueCustomerSave,
    UniqueCustomerReturnPayload,
    UniqueCustomerSaveReturnPayload,
} from '../../features/customer/customerAPI'
import { setCustomerName } from '../../features/customer/customerSlice'
import YellowTriangleWarning from '../../GlobalComponents/_icons/YellowTriangleWarning.svg'
import { AWCButton, BUTTON_TYPES } from '../../GlobalComponents/Inputs/Inputs'
import { fqAlert } from '../../GlobalComponents/NotificationManager/NotificationManager'
import styles from './UniqueCustomerNameModal.module.scss'

interface UniqueCustomerNameModalProps {
    close(): void
}

export const UniqueCustomerNameModal = (props: UniqueCustomerNameModalProps) => {
    const { close } = props
    const customer = useAppSelector((state) => state.customer.currentCustomer!)
    const [uniqueCustomer, setUniqueCustomer] = useState<UniqueCustomerReturnPayload>()
    async function getUniqueCustomer() {
        const tempCustomer: UniqueCustomerReturnPayload = await fetchUniqueCustomer(
            customer.AccountCode
        )
        setUniqueCustomer(tempCustomer)
    }
    useEffect(() => {
        getUniqueCustomer()
    }, [])

    return (
        <div
            className={
                uniqueCustomer?.UniqueCustomerId ||
                (customer.BillToCity?.toUpperCase() === customer.ShipToCity?.toUpperCase() &&
                    customer.BillToState?.toUpperCase() === customer.ShipToState?.toUpperCase())
                    ? styles.uniqueCustomerNameModalSingle
                    : styles.uniqueCustomerNameModal
            }
        >
            <div className={styles.uniqueCustomerNameModalHeader}>
                <div className={styles.uniqueCustomerNameTitle}>
                    Update Customer Info - {customer.CustomerName} ({customer.AccountCode.trim()})
                </div>
                <div onClick={close} className={'a-i-close'} style={{ marginTop: 2 }} />
            </div>

            <div className={'a-divider'} />

            <CustomerInformationUpdateBox
                showSingle={
                    !!uniqueCustomer?.UniqueCustomerId ||
                    (customer.BillToCity?.toUpperCase() === customer.ShipToCity?.toUpperCase() &&
                        customer.BillToState?.toUpperCase() === customer.ShipToState?.toUpperCase())
                }
                uniqueCustomer={uniqueCustomer}
                close={close}
            />
        </div>
    )
}

interface CustomerInformationUpdateBoxProps {
    showSingle: boolean
    uniqueCustomer?: UniqueCustomerReturnPayload
    close: () => void
}

const CustomerInformationUpdateBox = (props: CustomerInformationUpdateBoxProps) => {
    let { showSingle, uniqueCustomer, close } = props
    const customer = useAppSelector((state) => state.customer.currentCustomer!)
    const [notes, setNotes] = useState<string>()

    return (
        <div style={{ paddingLeft: 25 }}>
            <div style={{ display: 'flex', marginTop: 20, gap: 5 }}>
                <div style={{ display: 'block', width: 40 }}>
                    <img src={YellowTriangleWarning} alt="Warning" width={30} />
                </div>
                {uniqueCustomer && (
                    <div>
                        {showSingle ? (
                            <div>
                                <div className={styles.customerInformationTitle}>
                                    Customer Information Update
                                </div>
                                <div>Please confirm or edit customer information.</div>

                                <CustomerInformationSingleBox
                                    uniqueCustomer={uniqueCustomer}
                                    notes={notes}
                                    close={close}
                                    leftBox={true}
                                />
                            </div>
                        ) : (
                            <div>
                                <div className={styles.customerInformationTitle}>
                                    Customer Information Missing
                                </div>
                                <div>
                                    The billing address and default Ship To address do not match.
                                    Please choose the primary City, State (Site).
                                </div>

                                <div style={{ display: 'flex', gap: 50 }}>
                                    <CustomerInformationSingleBox
                                        uniqueCustomer={{
                                            ...uniqueCustomer,
                                            SiteName:
                                                `${customer.BillToCity} ${customer.BillToState}`.trim(),
                                        }}
                                        notes={notes}
                                        close={close}
                                        leftBox={true}
                                    />
                                    <CustomerInformationSingleBox
                                        uniqueCustomer={{
                                            ...uniqueCustomer,
                                            SiteName:
                                                `${customer.ShipToCity} ${customer.ShipToState}`.trim(),
                                        }}
                                        notes={notes}
                                        close={close}
                                        leftBox={false}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div style={{ display: 'flex', gap: 5, marginTop: 20, marginBottom: 20 }}>
                <div style={{ width: 40 }}>Notes</div>
                <TextArea
                    defaultValue={notes}
                    onChange={(event: TextAreaChangeEvent) => {
                        setNotes(event.value)
                    }}
                    style={{
                        width: showSingle ? 388 : 825,
                        minHeight: 120,
                        borderWidth: 1,
                        borderColor: 'lightgray',
                        borderRadius: 4,
                        backgroundColor: 'white',
                    }}
                />
            </div>
        </div>
    )
}

interface CustomerInformationBox {
    uniqueCustomer: UniqueCustomerReturnPayload
    notes?: string
    close: () => void
    leftBox: boolean
}

const CustomerInformationSingleBox = (props: CustomerInformationBox) => {
    const { uniqueCustomer, notes, close, leftBox } = props
    const customer = useAppSelector((state) => state.customer.currentCustomer!)
    const dispatch = useAppDispatch()

    // The values for the box
    const [companyName, setCompanyName] = useState<any>(
        { label: uniqueCustomer.EntityName || customer.BillToName, value: 1 } || ''
    )
    const handleCompanyNameTypingChange = (value: string, actionMeta: any) => {
        if (actionMeta?.action === 'input-blur') {
            if (actionMeta.prevInputValue) {
                clearBusinessAndPrimarySiteData()
                setCompanyName({ label: actionMeta.prevInputValue.substring(0, 50), value: 1 })
            }
        }
        if (value && value.length > 1) {
            getAutocompleteEntity(value.substring(0, 50))
        }
    }

    const handleCompanyNameSelection = (event: any) => {
        clearBusinessAndPrimarySiteData()
        if (event) {
            setCompanyName(event)
            if (typeof event.value === 'number') {
                // If picked one from data list
                getAutocompleteBusinessUnit(event.value)
                getAutocompleteSite(event.value)
            }
        } else {
            setCompanyName(null)
        }
    }

    const [businessUnit, setBusinessUnit] = useState<any>(
        uniqueCustomer.BusinessUnitName ? { label: uniqueCustomer.BusinessUnitName, value: 1 } : ''
    )
    const handleBusinessUnitChange = (event: any) => {
        if (event) {
            setBusinessUnit(event)
        } else {
            setBusinessUnit(null)
        }
    }
    const handleBusinessUnitTypingChange = (value: string, actionMeta: any) => {
        if (actionMeta?.action === 'input-blur') {
            if (actionMeta.prevInputValue) {
                setBusinessUnit({ label: actionMeta.prevInputValue.substring(0, 75), value: 1 })
            }
        }
    }

    const [primarySite, setPrimarySite] = useState<any>(
        uniqueCustomer.SiteName ? { label: uniqueCustomer.SiteName, value: 1 } : ''
    )
    const handlePrimarySiteChange = (event: any) => {
        if (event) {
            setPrimarySite(event)
        } else {
            setPrimarySite(null)
        }
    }
    const handlePrimarySiteTypingChange = (value: string, actionMeta: any) => {
        if (actionMeta?.action === 'input-blur') {
            if (actionMeta.prevInputValue) {
                setPrimarySite({ label: actionMeta.prevInputValue.substring(0, 50), value: 1 })
            }
        }
    }

    function clearBusinessAndPrimarySiteData() {
        setBusinessUnit('')
        setBusinessUnitData([])
        setPrimarySite('')
        setPrimarySiteData([])
    }

    // Data for the drop downs
    const [companyData, setCompanyData] = useState<AutocompleteEntityReturnPayload[]>([])
    const [businessUnitData, setBusinessUnitData] = useState<
        AutocompleteBusinessUnitReturnPayload[]
    >([])
    const [primarySiteData, setPrimarySiteData] = useState<AutocompleteSiteReturnPayload[]>([])

    // Get autocomplete information
    async function getAutocompleteEntity(companyName: string) {
        const tempData = await fetchAutocompleteEntity(companyName)
        setCompanyData(tempData)
        const tempRow = tempData.find((data: AutocompleteEntityReturnPayload) => {
            if (data.CustomerName === companyName) return data
        })
        if (tempRow) {
            getAutocompleteBusinessUnit(tempRow.CustomerEntityId)
            getAutocompleteSite(tempRow.CustomerEntityId)
        }
    }
    async function getAutocompleteBusinessUnit(customerEntityId: number) {
        const tempData = await fetchAutocompleteBusinessUnit(customerEntityId)
        setBusinessUnitData(tempData)
    }
    async function getAutocompleteSite(customerEntityId: number) {
        const tempData = await fetchAutocompleteSite(customerEntityId)
        setPrimarySiteData(tempData)
    }
    useEffect(() => {
        if (uniqueCustomer?.EntityName) {
            getAutocompleteEntity(uniqueCustomer.EntityName)
        } else {
            getAutocompleteEntity(customer.BillToName!)
        }
    }, [uniqueCustomer])

    return (
        <div>
            <div className={styles.uniqueCustomerNameCardContainer}>
                <AutocompleteTextInput
                    data={companyData.map((data: AutocompleteEntityReturnPayload) => {
                        return { value: data.CustomerEntityId, label: data.CustomerName }
                    })}
                    inputIndex={leftBox ? 1 : 5}
                    value={companyName}
                    handleChange={handleCompanyNameTypingChange}
                    handleSelection={handleCompanyNameSelection}
                    title={'Company Name (Entity):'}
                    required
                />
                <AutocompleteTextInput
                    data={businessUnitData.map((data: AutocompleteBusinessUnitReturnPayload) => {
                        return { value: data.CustomerBusinessUnitId, label: data.BusinessUnitName }
                    })}
                    inputIndex={leftBox ? 2 : 6}
                    value={businessUnit}
                    handleChange={handleBusinessUnitTypingChange}
                    handleSelection={handleBusinessUnitChange}
                    title={'Business Unit:'}
                />
                <AutocompleteTextInput
                    data={primarySiteData.map((data: AutocompleteSiteReturnPayload) => {
                        return { value: data.CustomerSiteId, label: data.SiteName }
                    })}
                    inputIndex={leftBox ? 3 : 7}
                    value={primarySite}
                    handleChange={handlePrimarySiteTypingChange}
                    handleSelection={handlePrimarySiteChange}
                    title={'Primary Site (City St):'}
                    required
                    isPrimarySite
                />

                <AWCButton
                    type={BUTTON_TYPES.PRIMARY}
                    tabIndex={leftBox ? 4 : 8}
                    onClick={async () => {
                        if (!companyName || !primarySite) {
                            fqAlert({
                                type: 'error',
                                message: 'Company name and primary site are both required!',
                                title: 'Save Failed.',
                            })
                        } else {
                            const resp: Response = await fetchUniqueCustomerSave({
                                AccountCode: customer.AccountCode,
                                UniqueCustomerId: uniqueCustomer.UniqueCustomerId,
                                EntityName: companyName.label,
                                BusinessUnitName: businessUnit?.label || null,
                                SiteName: primarySite.label,
                                Notes: notes || '',
                            })

                            if (resp.ok) {
                                let json: UniqueCustomerSaveReturnPayload = await resp.json()

                                fqAlert({
                                    type: 'success',
                                    message: 'Customer name update successful!',
                                })
                                close()
                                dispatch(setCustomerName([json.NewCustomerName, customer]))
                            } else {
                                fqAlert({
                                    type: 'error',
                                    message: 'Failed to save new customer name',
                                    title: 'Save failed',
                                })
                            }
                        }
                    }}
                    style={{ width: '100%', marginTop: 30 }}
                >
                    Confirm
                </AWCButton>
            </div>
        </div>
    )
}

const AutocompleteTextInput = (props: any) => {
    const {
        data,
        inputIndex,
        value,
        handleChange,
        handleSelection,
        title,
        required,
        isPrimarySite,
    } = props

    const handleKeyDown = useCallback((event: any) => {
        if (event.key === 'Enter') {
            event.target.blur()
        }
        if (event.key === 'Tab') {
            document.getElementById(`input-${inputIndex + 1}`)?.focus()
        }
    }, [])
    return (
        <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 16, display: 'flex', color: 'grey', marginBottom: 5 }}>
                {title}
            </div>
            <CreatableSelect
                isClearable
                id={`input-${inputIndex}`}
                className={'customerNameSelect'}
                key={`my_unique_key_${inputIndex}`}
                options={data}
                onChange={handleSelection}
                onInputChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder={''}
                tabSelectsValue={false}
                escapeClearsValue
                backspaceRemovesValue
                tabIndex={inputIndex}
                value={value}
                styles={{
                    control: (baseStyles, state) => ({
                        ...baseStyles,
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        fontFamily: 'roboto',
                    }),
                }}
            />
            <div
                style={{
                    fontSize: 12,
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: 5,
                }}
            >
                {isPrimarySite ? (
                    <span className={styles.cityStateNote}>
                        City State (ex: IRVING TX) - no comma
                    </span>
                ) : (
                    <span></span>
                )}
                {required && <span style={{ color: 'red', marginLeft: 5 }}>(Required)</span>}
            </div>
        </div>
    )
}
